import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, FormControl } from '@angular/forms';
import {
    SharedModule,
    StringCustomFieldConfig,
    FormInputComponent,
    DataService,
    NotificationService,
    Product,
    ProductVariant,
} from '@vendure/admin-ui/core';
import { safeJSONParse } from '../utils';
import { defaultProductInfoData, PageType, ProductInfoData, ProductSourceType } from '../ui-types';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'product-info-form',
    template: `
    <vdr-card>
      <div class="clr-row mb-2">
        <vdr-form-field [label]="'Select Product'" class="clr-col-12">
          <select [formControl]="productSourceControl">
            <option *ngIf="canUseInheritedProduct" value="dynamic">Dynamic</option>
            <option value="product">Product</option>
            <option value="variant">Product Variant</option>
          </select>
        </vdr-form-field>
        <div *ngIf="productSourceControl.value === 'dynamic'" class="clr-col-12 mt-1">
          <p class="hint-text">Automatically displays the product currently being viewed by the customer.</p>
        </div>
      </div>

      <div *ngIf="productSourceControl.value === 'product'" class="clr-row">
        <vdr-form-field [label]="'Product'" class="clr-col-12">
          <vdr-dynamic-form-input
              [control]="productControl"
              [def]="{ type: 'relation', entity: 'Product', ui: { component: 'relation-form-input' } }">
          </vdr-dynamic-form-input>
        </vdr-form-field>
      </div>

      <div *ngIf="productSourceControl.value === 'variant'" class="clr-row">
        <vdr-form-field [label]="'Product Variant'" class="clr-col-12">
          <vdr-dynamic-form-input
              [control]="productVariantControl"
              [def]="{ type: 'relation', entity: 'ProductVariant', ui: { component: 'relation-form-input' } }">
          </vdr-dynamic-form-input>
        </vdr-form-field>
      </div>
    </vdr-card>
    `,
    styleUrls: ['../styles.scss'],
    standalone: true,
    imports: [SharedModule],
})
export class ProductInfoComponent implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit, OnDestroy {
    readonly: boolean;
    config: StringCustomFieldConfig;
    formControl: FormControl;

    productSourceControl = new FormControl<ProductSourceType>('variant');
    productControl = new UntypedFormControl();
    productVariantControl = new UntypedFormControl();

    pageType: PageType;
    productInfoData: ProductInfoData;
    private destroyed$ = new Subject<void>();

    get canUseInheritedProduct(): boolean {
      return this.pageType === PageType.Product || this.pageType === PageType.ProductSpecific;
    }

    constructor(
      private notificationService: NotificationService,
      private changeDetector: ChangeDetectorRef,
      private dataService: DataService,
    ) {}

    ngOnInit() {
      this.productInfoData = safeJSONParse<ProductInfoData>(
          this.formControl.value,
          this.notificationService,
          defaultProductInfoData,
      )!;
      this.pageType = this.config.ui?.options?.pageType || PageType.General;
      this.initializeFormControls();
      this.handlePageTypeChange();
      this.config.ui?.changes?.pipe(takeUntil(this.destroyed$)).subscribe((data: any) => {
        if (data && data !== this.pageType) {
          this.pageType = data;
          this.handlePageTypeChange();
          this.changeDetector.detectChanges();
        }
      });

      if (this.productInfoData?.productId) {
        this.dataService.product.getProduct(this.productInfoData.productId.toString())
          .mapSingle(data => data.product)
          .pipe(takeUntil(this.destroyed$))
          .subscribe(product => this.productControl.setValue(product));
      }

      if (this.productInfoData?.productVariantId) {
        this.dataService.product.getProductVariant(this.productInfoData.productVariantId.toString())
          .mapSingle(data => data.productVariant)
          .pipe(takeUntil(this.destroyed$))
          .subscribe(productVariant => this.productVariantControl.setValue(productVariant));
      }
    }

    ngAfterViewInit() {
      this.productSourceControl.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(this.onProductSourceChange);
      this.productControl.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(this.onProductChange);
      this.productVariantControl.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(this.onProductVariantChange);
    }

    ngOnDestroy(): void {
      this.destroyed$.next();
      this.destroyed$.complete();
    }

    private initializeFormControls() {
      const sourceType = this.getSourceTypeFromData();
      this.productSourceControl.setValue(sourceType, { emitEvent: false });
    }

    private getSourceTypeFromData(): ProductSourceType {
      if (this.productInfoData?.sourceType) {
        return this.productInfoData.sourceType;
      }
      return this.productInfoData?.productId ? 'product' : 'variant';
    }

    private handlePageTypeChange() {
      if (this.productSourceControl.value === 'dynamic' && !this.canUseInheritedProduct) {
        this.productSourceControl.setValue('variant', { emitEvent: false });
        this.productInfoData.sourceType = 'variant';
        this.updateJsonString();
      }
    }

    private onProductSourceChange = (value: ProductSourceType | null) => {
      const normalized = value === 'dynamic' && !this.canUseInheritedProduct ? 'variant' : (value ?? 'variant');
      if (this.productInfoData?.sourceType !== normalized) {
        this.productInfoData!.sourceType = normalized;
        if (normalized === 'dynamic') {
          this.productControl.setValue(null, { emitEvent: false });
          this.productVariantControl.setValue(null, { emitEvent: false });
          this.productInfoData!.productId = null;
          this.productInfoData!.productVariantId = null;
        } else if (normalized === 'product') {
          this.productVariantControl.setValue(null, { emitEvent: false });
          this.productInfoData!.productVariantId = null;
        } else {
          this.productControl.setValue(null, { emitEvent: false });
          this.productInfoData!.productId = null;
        }
        this.updateJsonString();
      }
    }

    private onProductChange = (product: Product) => {
      const productId = product ? product.id : null;
      if (this.productInfoData?.productId !== productId) {
        this.productInfoData!.productId = productId;
        this.updateJsonString();
      }
    };

    private onProductVariantChange = (productVariant: ProductVariant) => {
      const productVariantId = productVariant ? productVariant.id : null;
      if (this.productInfoData?.productVariantId !== productVariantId) {
        this.productInfoData!.productVariantId = productVariantId;
        this.updateJsonString();
      }
    };

    private updateJsonString() {
      if (!this.canUseInheritedProduct && this.productInfoData.sourceType === 'dynamic') {
        this.productInfoData.sourceType = 'variant';
      }
      const jsonString = JSON.stringify(this.productInfoData);
      this.formControl.setValue(jsonString);
      this.formControl.markAsDirty();
    }
}
